// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/services/orcamentoService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type { PaginadoComResumo, OrcamentoListaResumo } from './listaFiltros';
import { paramsDaListaPessoa, type ListaFiltrosPessoa } from './davService';
import type {
  OrcamentoResumo,
  OrcamentoDetalhe,
  DavDetalhe,
  CriarOrcamentoRequest,
  AtualizarOrcamentoRequest,
} from '../types';
import { paramsConsulta, type ComVendedor } from '../consultas';

const BASE = '/api/orcamentos';

/** KPIs de GET /api/orcamentos/resumo — consulta inteira, sem o status (status exclusivos). */
export interface OrcamentoKpi {
  total: number;
  abertos: number;
  valorAbertos: number;
  aprovados: number;
  expirados: number;
  devolvidos: number;
  valorTotal: number;
}

export const orcamentoService = {
  /**
   * Filtros no servidor: busca, status (aberto|aprovado|expirado|devolvido), período, cliente,
   * vendedor e ordenação. Traz `resumo` (KPIs sem o status) e o nome do vendedor em cada linha.
   */
  async getAll(
    pagina = 1, tamanhoPagina = 20, filtros: ListaFiltrosPessoa = {},
  ): Promise<PaginadoComResumo<ComVendedor<OrcamentoResumo>, OrcamentoListaResumo>> {
    const { data } = await apiClient.get<PaginadoComResumo<ComVendedor<OrcamentoResumo>, OrcamentoListaResumo>>(BASE, {
      params: paramsDaListaPessoa(pagina, tamanhoPagina, filtros),
    });
    return data;
  },

  /** KPIs sobre o filtro inteiro (ignora status). */
  async getResumo(filtros: ListaFiltrosPessoa = {}): Promise<OrcamentoKpi> {
    const { busca, dtInicio, dtFim, clienteId, vendedorId } = filtros;
    const { data } = await apiClient.get<OrcamentoKpi>(`${BASE}/resumo`, {
      params: paramsConsulta({ busca: busca?.trim(), dtInicio, dtFim, clienteId, vendedorId }),
    });
    return data;
  },

  async getAbertos(): Promise<OrcamentoResumo[]> {
    const { data } = await apiClient.get<OrcamentoResumo[]>(`${BASE}/abertos`);
    return data;
  },

  async getPorPeriodo(inicio: string, fim: string): Promise<OrcamentoResumo[]> {
    const { data } = await apiClient.get<OrcamentoResumo[]>(`${BASE}/periodo`, {
      params: { inicio, fim },
    });
    return data;
  },

  async getById(id: number): Promise<OrcamentoDetalhe> {
    const { data } = await apiClient.get<OrcamentoDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async create(request: CriarOrcamentoRequest): Promise<OrcamentoDetalhe> {
    const { data } = await apiClient.post<OrcamentoDetalhe>(BASE, request);
    return data;
  },

  async update(id: number, request: AtualizarOrcamentoRequest): Promise<OrcamentoResumo> {
    const { data } = await apiClient.put<OrcamentoResumo>(`${BASE}/${id}`, request);
    return data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async converterEmDav(id: number): Promise<DavDetalhe> {
    const { data } = await apiClient.post<DavDetalhe>(`${BASE}/${id}/converter-dav`);
    return data;
  },
};
